import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import { api, type User } from "../api/client";
import { icons } from "../icons";
import { PUBLIC_ROUTES, interceptLinks, match, navigate, safeNext, type Match } from "../router";
import { shared } from "../styles/shared";

import "../pages/page-docs";
import "../pages/page-history";
import "../pages/page-login";
import "../pages/page-report";
import "../pages/page-search";

const TITLES: Record<Match["name"], string> = {
  login: "Connexion",
  search: "Recherche",
  report: "Rapport",
  history: "Historique",
  docs: "Documentation",
  "not-found": "Page introuvable",
};

/** Application shell: session, routing, header and footer. */
@customElement("ld-app")
export class LdApp extends LitElement {
  @state() private route: Match = match(location.pathname);
  /** undefined = session still being checked */
  @state() private user: User | null | undefined = undefined;
  @state() private menuOpen = false;

  static styles = [
    shared,
    css`
      :host { display: flex; flex-direction: column; min-height: 100vh; }
      main { flex: 1; }
      header {
        position: sticky; top: 0; z-index: 100; height: var(--header-h); display: flex; align-items: center;
        background: rgba(255, 255, 255, .82); backdrop-filter: blur(10px) saturate(1.4);
        border-bottom: 1px solid var(--slate-200);
      }
      header .container { display: flex; align-items: center; justify-content: space-between; gap: 24px; }
      .brand { display: flex; align-items: center; gap: 10px; font-weight: 700; font-size: 17px; letter-spacing: -.01em; }
      .brand-mark {
        width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0; box-shadow: var(--shadow-sm);
        background: linear-gradient(155deg, var(--blue-500), var(--navy-900));
        display: flex; align-items: center; justify-content: center;
      }
      nav { display: flex; align-items: center; gap: 4px; }
      nav a { padding: 8px 14px; border-radius: var(--r-pill); font-size: 14px; font-weight: 500; color: var(--slate-600); transition: background .18s var(--ease), color .18s var(--ease); }
      nav a:hover { background: var(--slate-100); color: var(--ink); }
      nav a[aria-current="page"] { background: var(--blue-100); color: var(--blue-600); }
      .actions { display: flex; align-items: center; gap: 10px; }
      .avatar {
        width: 36px; height: 36px; border-radius: var(--r-pill); background: var(--navy-900); color: var(--white);
        display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 600;
      }
      .menu-toggle { display: none; }
      @media (max-width: 860px) {
        nav { display: none; }
        nav.open {
          display: flex; position: absolute; top: var(--header-h); left: 0; right: 0; flex-direction: column;
          align-items: stretch; gap: 2px; padding: 12px; background: var(--white); border-bottom: 1px solid var(--slate-200);
        }
        .menu-toggle { display: inline-flex; }
        .cta { display: none; }
      }
      footer { border-top: 1px solid var(--slate-200); padding: 32px 0; margin-top: 40px; }
      footer .container { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px; }
      .footer-brand { display: flex; align-items: center; gap: 10px; font-weight: 700; font-size: 14px; }
      .footer-note { font-size: 12px; color: var(--slate-400); }
      .loading { display: flex; justify-content: center; padding: 120px 0; color: var(--slate-400); }
      .not-found { padding: 80px 0; }
    `,
  ];

  connectedCallback(): void {
    super.connectedCallback();
    interceptLinks();
    window.addEventListener("popstate", this.onLocation);
    window.addEventListener("session-expired", this.onSessionExpired);
    this.addEventListener("logged-in", this.onLoggedIn);
    void this.loadSession();
  }

  disconnectedCallback(): void {
    window.removeEventListener("popstate", this.onLocation);
    window.removeEventListener("session-expired", this.onSessionExpired);
    super.disconnectedCallback();
  }

  private onLocation = () => {
    this.route = match(location.pathname);
    this.menuOpen = false;
    this.guard();
  };

  private onSessionExpired = () => {
    this.user = null;
    this.guard();
  };

  private onLoggedIn = async () => {
    await this.loadSession(); // guard() then sends the user to the requested page
  };

  private async loadSession() {
    try {
      this.user = await api.me();
    } catch {
      this.user = null;
    }
    this.guard();
  }

  /** Redirects to the login page when needed. Real access control is done by the API. */
  private guard() {
    if (this.user === undefined) return;
    const isPublic = PUBLIC_ROUTES.includes(this.route.name);
    if (this.user === null && !isPublic) {
      const suite = encodeURIComponent(location.pathname + location.search);
      navigate(`/connexion?suite=${suite}`, { replace: true });
    } else if (this.user && this.route.name === "login") {
      navigate(safeNext(location.search), { replace: true });
    }
  }

  private async logout() {
    await api.logout().catch(() => undefined);
    this.user = null;
    navigate("/connexion");
  }

  protected updated(): void {
    document.title = `${TITLES[this.route.name]} · Laundering`;
  }

  private renderPage() {
    const { name, params } = this.route;
    if (this.user === undefined) return html`<div class="loading"><span class="spinner"></span></div>`;
    if (!this.user && name !== "login") return nothing;
    switch (name) {
      case "login":
        return html`<page-login></page-login>`;
      case "search":
        return html`<page-search></page-search>`;
      case "report":
        return html`<page-report .screeningId=${params.id!}></page-report>`;
      case "history":
        return html`<page-history .user=${this.user!}></page-history>`;
      case "docs":
        return html`<page-docs></page-docs>`;
      default:
        return html`<div class="container not-found">
          <div class="page-eyebrow">Erreur 404</div>
          <h1 class="page-title">Page introuvable</h1>
          <p class="page-sub"><a class="btn btn-secondary" href="/">Retour à la recherche</a></p>
        </div>`;
    }
  }

  private navLink(href: string, label: string, active: boolean) {
    return html`<a href=${href} aria-current=${active ? "page" : "false"}>${label}</a>`;
  }

  private renderHeader() {
    if (!this.user) return nothing;
    const name = this.route.name;
    return html`<header>
      <div class="container">
        <a href="/" class="brand"><span class="brand-mark">${icons.brand}</span>Laundering</a>
        <nav class=${this.menuOpen ? "open" : ""} aria-label="Navigation principale">
          ${this.navLink("/", "Recherche", name === "search" || name === "report")}
          ${this.navLink("/historique", "Historique", name === "history")}
          ${this.navLink("/documentation", "Documentation", name === "docs")}
        </nav>
        <div class="actions">
          <a href="/" class="btn btn-primary btn-sm cta">${icons.search} Lancer une recherche</a>
          <span class="avatar" title="${this.user.display_name} · ${this.user.sees_whole_team ? "superviseur" : "enquêteur"}">${this.user.initials}</span>
          <button class="icon-btn" title="Se déconnecter" aria-label="Se déconnecter" @click=${this.logout}>${icons.logout}</button>
          <button class="icon-btn menu-toggle" aria-label="Menu" aria-expanded=${this.menuOpen} @click=${() => (this.menuOpen = !this.menuOpen)}>${icons.menu}</button>
        </div>
      </div>
    </header>`;
  }

  render() {
    return html`
      ${this.renderHeader()}
      <main>${this.renderPage()}</main>
      <footer>
        <div class="container">
          <div class="footer-brand"><span class="brand-mark" style="width:26px;height:26px">${icons.brand}</span>Laundering</div>
          <div class="footer-note">Outil d'aide au criblage : les voyants signalent, l'enquêteur conclut.</div>
        </div>
      </footer>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ld-app": LdApp;
  }
}
